// src/context/ThemeContext.js
import { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext(null);
const STORAGE_KEY = 'theme';

export function resolveEffectiveTheme(theme) {
    if (theme !== 'auto') return theme;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(effectiveTheme) {
    document.documentElement.setAttribute('data-bs-theme', effectiveTheme);
    // Sync the inline backgroundColor set by the blocking script in index.html
    document.documentElement.style.backgroundColor = effectiveTheme === 'dark' ? '#212529' : '';
}

export function ThemeProvider({ children }) {
    const [theme, setThemeState] = useState(() => {
        return localStorage.getItem(STORAGE_KEY) || 'auto';
    });

    const [effectiveTheme, setEffectiveTheme] = useState(() =>
        resolveEffectiveTheme(localStorage.getItem(STORAGE_KEY) || 'auto')
    );

    useEffect(() => {
        const effective = resolveEffectiveTheme(theme);
        applyTheme(effective);

        if (theme === 'auto') {
            const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
            const handler = (e) => {
                const resolved = e.matches ? 'dark' : 'light';
                applyTheme(resolved);
                setEffectiveTheme(resolved);
            };
            mediaQuery.addEventListener('change', handler);
            return () => mediaQuery.removeEventListener('change', handler);
        } else {
            setEffectiveTheme(effective);
        }
        // No cleanup for non-auto: data-bs-theme is intentionally left on document.documentElement
        // since ThemeProvider lives for the app's lifetime ("set and forget").
    }, [theme]);

    function setTheme(newTheme) {
        localStorage.setItem(STORAGE_KEY, newTheme);
        setThemeState(newTheme);
    }

    return (
        <ThemeContext.Provider value={{ theme, setTheme, effectiveTheme }}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useTheme() {
    const ctx = useContext(ThemeContext);
    if (!ctx) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return ctx;
}
